import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PLATFORM_COLORS, hunterTier } from "../lib/constants";
import { Loader2, Crosshair, ArrowLeft, ShieldX, Target } from "lucide-react";

export default function HunterProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/hunters/${userId}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [userId]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center" data-testid="hunter-not-found">
        <ShieldX className="mx-auto mb-3 text-[#334155]" size={40} />
        <p className="text-[#94A3B8]">This hunter profile doesn't exist.</p>
        <button onClick={() => navigate("/stats")} className="mt-4 text-sm font-semibold text-[#00F0FF] hover:underline">← Back to Stats</button>
      </div>
    );
  }

  if (!data) return <div className="flex justify-center py-32 text-[#64748B]"><Loader2 className="animate-spin" /></div>;

  const tier = hunterTier(data.busts) || { label: "Hunter", color: "#00FF87" };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="hunter-profile">
      <button data-testid="hunter-back-btn" onClick={() => navigate(-1)} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#94A3B8] hover:text-white">
        <ArrowLeft size={15} /> Back
      </button>

      {/* Header */}
      <div className="animate-fade-up relative overflow-hidden rounded-2xl border p-6 sm:p-8" style={{ borderColor: `${tier.color}4D`, backgroundColor: `${tier.color}0F` }}>
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl" style={{ backgroundColor: `${tier.color}1A` }} />
        <div className="relative flex flex-wrap items-center gap-5">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl" style={{ backgroundColor: `${tier.color}26`, color: tier.color }}>
            <Crosshair size={30} />
          </div>
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: tier.color, backgroundColor: `${tier.color}26` }}>
              {tier.label}
            </div>
            <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">{data.name}</h1>
            <p className="mt-1 text-sm text-[#94A3B8]">Certified Hunter keeping NBA 2K lobbies clean.</p>
          </div>
          <div className="ml-auto text-right">
            <div className="font-heading text-4xl font-black" style={{ color: tier.color }}>{data.busts}</div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#64748B]">confirmed bust{data.busts === 1 ? "" : "s"}</div>
          </div>
        </div>
      </div>

      {/* Cheaters taken down */}
      <div className="animate-fade-up mt-8">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-white"><Target size={18} className="text-[#FF2A55]" /> Cheaters they helped take down</h2>
        {data.cheaters.length === 0 ? (
          <p className="mt-3 rounded-xl border border-dashed border-[#1E293F] bg-[#0F1424] py-12 text-center text-[#64748B]">No confirmed busts yet.</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {data.cheaters.map((c) => (
              <button
                key={c.id}
                data-testid={`hunter-cheater-${c.id}`}
                onClick={() => navigate(`/players/${c.platform}/${encodeURIComponent(c.gamertag)}`)}
                className="flex items-center gap-3 rounded-xl border border-[#1E293F] bg-[#141B2D] p-3 text-left transition-all hover:border-[#FF2A55]/50 hover:bg-[#1A233A]"
              >
                <ShieldX size={18} className="shrink-0 text-[#FF2A55]" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-heading text-sm font-bold text-white">{c.gamertag}</div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="font-semibold" style={{ color: PLATFORM_COLORS[c.platform] || "#94A3B8" }}>{c.platform}</span>
                    <span className="text-[#64748B]">·</span>
                    <span className="truncate text-[#94A3B8]">{c.cheat_type}</span>
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-[#FF2A55]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-[#FF2A55]">Confirmed</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
